<template>
  <div
    :class="[
      'game',
      'game-syllables',
      (status !== undefined || showSolution) && 'game-finished',
    ]"
  >
    <h2>Syllables</h2>
    <div
      v-if="selectedWord"
      :class="`word-${gameSrcLang}`"
      :title="selectedWord[gameTargetLang]"
    >
      {{ selectedWord[gameSrcLang] }}
    </div>
    <div v-if="selectedWord" :class="`word-target word-${gameTargetLang}`">
      <div
        v-for="(letter, index) in selectedWord[gameTargetLang]"
        :class="letter !== ' ' ? 'letter' : 'blank'"
        :key="`letter-${index}`"
        :title="letter"
      >
        <div v-if="!showSolution && index < enteredText.length">
          {{ enteredText[index] }}
        </div>
        <div v-if="showSolution">{{ letter }}</div>
      </div>
    </div>
    <div v-if="status" :class="'word-' + gameTargetLang">
      <icelandic-term :term="selectedWord[gameTargetLang]" />
    </div>
    <div v-if="selectedWord" ref="syllables" class="syllables">
      <div
        v-for="(syllable, index) in shuffledSyllables"
        ref="syllable"
        class="syllable"
        :key="`syllable-${index}`"
        :title="syllable"
        @click="onClick(syllable, index)"
      >
        {{ syllable }}
      </div>
    </div>
  </div>
</template>

<script>
import IcelandicTerm from "@/components/IcelandicTerm.vue";
import { mapGetters } from "vuex";
const helpers = require("@/services/helpers.js");

const SYLLABLES_POSSIBLE_LENGTHS = [];
SYLLABLES_POSSIBLE_LENGTHS.push([]); // 0
SYLLABLES_POSSIBLE_LENGTHS.push([1]); // 1
SYLLABLES_POSSIBLE_LENGTHS.push([2, 2, 1]); // 2
SYLLABLES_POSSIBLE_LENGTHS.push([3, 3, 3, 2, 2, 1, 1]); // 3

export default {
  name: "GameSyllables",
  components: { IcelandicTerm },
  data() {
    const emptyWord = {};
    emptyWord[this.gameSrcLang] = "";
    emptyWord[this.gameTargetLang] = "";
    return {
      syllables: [],
      shuffledSyllables: [],
      enteredSyllables: [],
      enteredText: "",
      status: undefined,
      errors: 0,
      emptyWord,
      showSolution: false,
    };
  },
  computed: {
    ...mapGetters([
      "gameSrcLang",
      "gameTargetLang",
      "gameAllowedErrors",
      "selectedWords",
    ]),
    selectedWord() {
      if (this.selectedWords.length == 0) return this.emptyWord;
      for (let word of this.selectedWords) {
        if (word.failed) return word;
      }
      return this.selectedWords[0];
    },
  },
  mounted() {
    this.initGame();
  },
  created() {
    this.$store.subscribe((mutation, state) => {
      if (["CATEGORY_SELECTED", "NEW_GAME"].includes(mutation.type)) {
        this.initGame();
      }
    });
  },
  updated() {
    this.layoutSyllables();
  },
  methods: {
    splitWord(word) {
      let syllables = [];
      let syllableMaxLength;
      if (word.length > 6) {
        syllableMaxLength = 3;
      } else if (word.length > 2) {
        syllableMaxLength = 2;
      } else {
        syllableMaxLength = 1;
      }
      let chars = word;
      while (chars) {
        let idxLen = helpers.randomInteger(
          0,
          SYLLABLES_POSSIBLE_LENGTHS[syllableMaxLength].length - 1
        );
        let len = SYLLABLES_POSSIBLE_LENGTHS[syllableMaxLength][idxLen];
        let syllable = chars.substring(0, len);
        if (syllable.endsWith(" ")) {
          syllable = syllable.substring(0, syllable.length - 1);
        }
        let pos = syllable.indexOf(" ");
        if (pos != -1) {
          syllable = syllable.substring(0, pos);
          len = pos;
        }
        if (syllable) {
          syllables.push(syllable);
        }
        chars = chars.substring(len).trim();
      }
      return syllables;
    },
    onClick(syllable, index) {
      if (this.showSolution) return;

      if (this.enteredSyllables.length == this.syllables.length) return;

      let currentSyllables = this.enteredSyllables.slice(0);
      currentSyllables.push(syllable);

      let subSyllables = this.syllables.slice(0, currentSyllables.length);

      if (helpers.arraysAreEqual(currentSyllables, subSyllables)) {
        if (this.syllables.length > currentSyllables.length) {
          this.setGameStatus(undefined);
        } else {
          this.setGameStatus(true);
        }

        this.enteredSyllables = currentSyllables;
        this.enteredText += syllable;
        if (
          this.selectedWord[this.gameTargetLang].length >
            this.enteredText.length &&
          this.selectedWord[this.gameTargetLang][this.enteredText.length] ===
            " "
        ) {
          this.enteredText += " ";
        }

        let pos = index; // this.shuffledSyllables.findIndex((str) => str === syllable);
        this.shuffledSyllables.splice(pos, 1);
      } else {
        this.setGameStatus(false);
      }
    },
    initGame() {
      console.log(
        `GameSyllables initGame ${this.gameSrcLang.toUpperCase()} to ${this.gameTargetLang.toUpperCase()}: ${this.selectedWord[this.gameTargetLang]}`
      );

      this.syllables = this.splitWord(this.selectedWord[this.gameTargetLang]);

      this.shuffledSyllables = this.syllables.slice(0);
      if (helpers.randomInteger(1, 10) > 5) {
        // add syllables from another word, for confusion
        for (const otherWord of this.selectedWords) {
          if (
            otherWord[this.gameTargetLang] !==
            this.selectedWord[this.gameTargetLang]
          ) {
            let otherSyllables = this.splitWord(otherWord[this.gameTargetLang]);
            let otherSyllablesMaxLength = Math.max(
              1,
              Math.floor(this.shuffledSyllables.length / 2)
            );
            if (otherSyllables.length > otherSyllablesMaxLength) {
              otherSyllables = otherSyllables.slice(0, otherSyllablesMaxLength);
            }
            this.shuffledSyllables =
              this.shuffledSyllables.concat(otherSyllables);
            break;
          }
        }
      }

      helpers.shuffleArray(this.shuffledSyllables);

      this.showSolution = false;
      this.enteredSyllables = [];
      this.enteredText = "";
      this.setGameStatus(undefined);
      this.errors = 0;
    },
    setGameStatus(status) {
      if (status === false) {
        this.errors++;
        if (this.errors < this.gameAllowedErrors) return;
      }
      this.status = status;
      this.$emit("gameStatus", this.status);
    },
    layoutSyllables() {
      if (!this.$refs.syllables) return;

      // https://stackoverflow.com/questions/27732875/randomly-positioned-divs-with-no-overlapping
      let mainRect = window.getComputedStyle(this.$refs.syllables);

      // for (let syllable of this.$refs.syllable) {
      //   let conflict = true;
      //   while (conflict) {
      //     fixLeft = Math.floor(Math.random() * x) * 10 + dx;
      //     fixTop = Math.floor(Math.random() * 40) * 10 + 180;
      //     $(this).offset({
      //       left: fixLeft,
      //       top: fixTop,
      //     });
      //     var box = {
      //       top: parseInt(window.getComputedStyle($(this)[0]).top),
      //       left: parseInt(window.getComputedStyle($(this)[0]).left),
      //       width: parseInt(window.getComputedStyle($(this)[0]).width),
      //       height: parseInt(window.getComputedStyle($(this)[0]).height),
      //     };
      //     conflict = false;
      //     for (var i = 0; i < boxDims.length; i++) {
      //       if (overlap(box, boxDims[i])) {
      //         conflict = true;
      //         break;
      //       } else {
      //         conflict = false;
      //       }
      //     }
      //   }
      //   boxDims.push(box);
      // }
    },
    solution() {
      this.showSolution = true;
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@import "@/scss/components-shared.scss";

.game-syllables {
  display: flex;
  flex-direction: column;
}
.word-target {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
}

.word-is {
  margin-top: 2rem;
}

.letter,
.blank {
  height: 32px;
  width: 1.5rem;
  line-height: 32px;
}
.letter {
  margin-left: 0.25rem;
  margin-right: 0.25rem;
  border-bottom: 4px solid #555;
}
.game-finished .letter {
  border-bottom-color: orange;
}
.syllables {
  flex: 1;
  padding: 2rem;
}
.syllable {
  display: inline-block;
  background-color: #ccc;
  height: 32px;
  line-height: 32px;
  border-radius: 16px;
  min-width: 32px;
  cursor: default;
  padding-left: 8px;
  padding-right: 8px;
  margin: 0.5rem;
}
</style>
